import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, ArrowLeft, ArrowRight, Tag, Share2 } from 'lucide-react';
import { blogPosts as mockBlogPosts } from '../mockData';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const [blogPosts, setBlogPosts] = useState(mockBlogPosts);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blogs`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setBlogPosts(data);
          }
        }
      } catch (error) {
        console.log('Using mock blog data');
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);
  
  const post = blogPosts.find(p => p.slug === slug);
  const currentIndex = blogPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  if (!post) {
    return (
      <div className="page-container">
        <section className="section-container">
          <div className="content-wrapper text-center-content">
            <h1 className="page-title">Article Not Found</h1>
            <p className="section-description">The article you're looking for doesn't exist.</p>
            <Link to="/blog" className="btn-primary">
              <ArrowLeft size={20} /> Back to Blog
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link Copied!",
        description: "Article link copied to clipboard",
      });
    }
  };

  // Article Schema for this blog post
  const articleSchema = post ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": {
      "@type": "Organization",
      "name": "Hair Extensions Training Academy",
      "url": "https://hairextensionstraining.academy"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Hair Extensions Training Academy",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_a235b419-86b2-4667-9fd0-c39959dca4de/artifacts/he7jel3z_hair%20extensions%20training%20academy%20logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://hairextensionstraining.academy/blog/${post.slug}`
    },
    "keywords": post.tags.join(", ")
  } : null;

  return (
    <div className="page-container" data-testid="blog-post-page">
      {post && (
        <Helmet>
          <title>{post.title} | Hair Extensions Training Academy</title>
          <meta name="description" content={post.excerpt} />
          <link rel="canonical" href={`https://hairextensionstraining.academy/blog/${post.slug}`} />
          <meta property="og:title" content={post.title} />
          <meta property="og:description" content={post.excerpt} />
          <meta property="og:image" content={post.image} />
          <meta property="og:type" content="article" />
          <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        </Helmet>
      )}

      {/* Hero Banner */}
      <section className="blog-post-hero">
        <img src={post.image} alt={post.title} className="blog-post-hero-image" />
        <div className="blog-post-hero-overlay">
          <div className="content-wrapper">
            <Link to="/blog" className="blog-back-link">
              <ArrowLeft size={18} /> Back to Blog
            </Link>
            <h1 className="blog-post-title">{post.title}</h1>
            <div className="blog-post-meta">
              <span className="blog-post-date">
                <Calendar size={16} />
                {new Date(post.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
              <button onClick={handleShare} className="blog-share-btn">
                <Share2 size={16} /> Share
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="section-container">
        <div className="content-wrapper">
          <article className="blog-post-content">
            {post.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="blog-post-paragraph">{paragraph}</p>
            ))}
          </article>

          {/* Tags */}
          <div className="blog-post-tags">
            {post.tags.map((tag, index) => (
              <span key={index} className="blog-tag-large">
                <Tag size={14} />
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry CTA */}
      <section className="section-container blog-enquiry-cta">
        <div className="content-wrapper">
          <div className="enquiry-cta-box">
            <h2 className="enquiry-cta-title">Interested in Training?</h2>
            <p className="enquiry-cta-text">
              Take the first step towards a rewarding career. Submit an enquiry to learn more about our CPD-accredited courses.
            </p>
            <Link to="/courses" className="btn-primary-large" data-testid="blog-enquiry-btn">
              Enquire About Courses <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="section-container section-alt">
        <div className="content-wrapper">
          <div className="blog-post-navigation">
            {prevPost ? (
              <Link to={`/blog/${prevPost.slug}`} className="blog-nav-link prev">
                <ArrowLeft size={20} />
                <div className="blog-nav-text">
                  <span className="blog-nav-label">Previous Article</span>
                  <span className="blog-nav-title">{prevPost.title}</span>
                </div>
              </Link>
            ) : <div />}
            
            {nextPost ? (
              <Link to={`/blog/${nextPost.slug}`} className="blog-nav-link next">
                <div className="blog-nav-text">
                  <span className="blog-nav-label">Next Article</span>
                  <span className="blog-nav-title">{nextPost.title}</span>
                </div>
                <ArrowRight size={20} />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
